import React, { useEffect, useState } from "react";
import Layout, { PageHeader } from "../components/Layout";
import api, { inr, today, formatApiError, hasWrite } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Search, Plus, X, Printer, Layers } from "lucide-react";
import { toast } from "sonner";

const field = "w-full h-10 px-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40";
const label = "block text-sm font-medium mb-1.5";
const PAY_METHODS = ["cash", "bank", "upi", "qr"];

export default function PendingPayments() {
  const { user } = useAuth();
  const canWrite = hasWrite(user);
  const [invoices, setInvoices] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(new Set());

  const [pay, setPay] = useState(null);
  const [payDate, setPayDate] = useState(today());
  const [payNote, setPayNote] = useState("");
  const [rows, setRows] = useState([{ method: "cash", amount: "", reference: "" }]);

  const [bulk, setBulk] = useState(null); // { party, invoices, total }
  const [bulkDate, setBulkDate] = useState(today());
  const [bulkMethod, setBulkMethod] = useState("cash");
  const [bulkRef, setBulkRef] = useState("");
  const [bulkAmount, setBulkAmount] = useState("");
  const [bulkNote, setBulkNote] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = { statuses: "unpaid,partial" };
      if (search.trim()) params.search = search.trim();
      const { data } = await api.get("/invoices", { params });
      setInvoices((data || []).filter((i) => i.balance > 0));
      setSel(new Set());
    } catch (e) { toast.error("Could not load pending payments"); }
    setLoading(false);
  };
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const totalOutstanding = invoices.reduce((s, i) => s + i.balance, 0);
  const toggleSel = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectedInvoices = invoices.filter((i) => sel.has(i.id));
  const selectedParties = [...new Set(selectedInvoices.map((i) => i.party_name))];
  const selectedTotal = selectedInvoices.reduce((s, i) => s + i.balance, 0);

  // ---- single invoice payment ----
  const openPay = (inv) => {
    setPay(inv); setPayDate(today()); setPayNote("");
    setRows([{ method: "cash", amount: String(inv.balance), reference: "" }]);
  };
  const addRow = () => setRows((r) => [...r, { method: "bank", amount: "", reference: "" }]);
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const setRow = (i, patch) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const payTotal = rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);

  const submitPay = async (print = false) => {
    try {
      const splits = rows.filter((r) => parseFloat(r.amount) > 0)
        .map((r) => ({ method: r.method, amount: Math.round(parseFloat(r.amount) * 100) / 100, reference: r.reference || "" }));
      if (splits.length === 0) return toast.error("Enter at least one amount");
      if (splits.some((s) => s.method !== "cash" && !s.reference.trim())) return toast.error("Add a reference number for online (bank/UPI/QR) payments");
      const total = Math.round(splits.reduce((s, r) => s + r.amount, 0) * 100) / 100;
      if (total > pay.balance + 0.001) return toast.error(`Total ${inr(total)} exceeds balance ${inr(pay.balance)}`);
      const { data } = await api.post(`/invoices/${pay.id}/payments`, { date: payDate, note: payNote, splits });
      toast.success("Payment recorded · receipt generated");
      if (print && data.receipt_id) await openPdf(data.receipt_id);
      setPay(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ---- bulk settle ----
  const openBulk = () => {
    if (selectedInvoices.length === 0) return;
    if (selectedParties.length > 1) return toast.error("Select invoices of the same party to settle together");
    setBulk({ party: selectedParties[0], invoices: selectedInvoices, total: selectedTotal });
    setBulkDate(today()); setBulkMethod("cash"); setBulkRef(""); setBulkAmount(String(Math.round(selectedTotal * 100) / 100)); setBulkNote("");
  };
  const submitBulk = async (print = false) => {
    try {
      const amt = parseFloat(bulkAmount);
      if (isNaN(amt) || amt <= 0) return toast.error("Enter a valid amount");
      if (amt > bulk.total + 0.01) return toast.error(`Amount exceeds total balance ${inr(bulk.total)}`);
      if (bulkMethod !== "cash" && !bulkRef.trim()) return toast.error("Add a reference number for non-cash payments");
      const { data } = await api.post(`/invoices/bulk-payment`, {
        party_name: bulk.party, date: bulkDate, note: bulkNote,
        invoice_ids: bulk.invoices.map((i) => i.id), method: bulkMethod,
        reference: bulkRef, amount: amt,
      });
      toast.success(`Settled ${data.allocations?.length || bulk.invoices.length} invoice(s) · receipt ${data.number}`);
      if (print && data.id) await openPdf(data.id);
      setBulk(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openPdf = async (vid) => {
    try {
      const res = await api.get(`/vouchers/${vid}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data));
    } catch { /* ignore */ }
  };

  return (
    <Layout>
      <div className="p-8 max-w-5xl mx-auto">
        <PageHeader title="Pending Payments" subtitle="Invoices awaiting payment. Record a payment to generate its receipt." />

        <div className="grid grid-cols-2 gap-3 mb-5">
          <div className="rounded-lg border border-border bg-card px-4 py-3">
            <div className="text-xs text-muted-foreground">Pending invoices</div>
            <div data-testid="pending-count" className="num text-xl font-bold">{invoices.length}</div>
          </div>
          <div className="rounded-lg border border-destructive/20 bg-destructive/5 px-4 py-3">
            <div className="text-xs text-muted-foreground">Total outstanding</div>
            <div data-testid="pending-total" className="num text-xl font-bold text-destructive">{inr(totalOutstanding)}</div>
          </div>
        </div>

        <div className="relative mb-5 max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="pending-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by invoice no, account, vehicle or driver mobile…"
            className="w-full h-10 pl-9 pr-3 rounded-md border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring/40" />
        </div>

        {canWrite && sel.size > 0 && (
          <div data-testid="pending-bulk-bar" className="flex flex-wrap items-center gap-3 mb-4 rounded-lg border border-accent/30 bg-accent/5 px-4 py-2.5">
            <span className="text-sm font-medium">{sel.size} selected · <span className="num text-destructive font-bold">{inr(selectedTotal)}</span></span>
            {selectedParties.length > 1 && <span className="text-xs text-destructive">Select one party only</span>}
            <button data-testid="pending-bulk-settle" onClick={openBulk} disabled={selectedParties.length > 1}
              className="ml-auto h-9 px-4 rounded-md bg-accent text-accent-foreground text-sm font-medium flex items-center gap-2 hover:bg-accent/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              <Layers size={15} /> Settle in one receipt
            </button>
            <button data-testid="pending-bulk-clear" onClick={() => setSel(new Set())} className="h-9 px-3 rounded-md border border-input bg-card text-xs hover:bg-muted transition-colors">Clear</button>
          </div>
        )}

        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="grid grid-cols-[auto_auto_1.4fr_auto_auto_auto] gap-3 px-5 py-3 bg-muted text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <span></span><span>No.</span><span>Account / vehicle</span><span className="text-right">Amount</span><span className="text-right">Balance</span><span className="text-right">Action</span>
          </div>
          <div className="divide-y divide-border max-h-[560px] overflow-auto">
            {loading ? (
              <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
            ) : invoices.length === 0 ? (
              <div data-testid="pending-empty" className="p-10 text-center text-sm text-muted-foreground">No pending payments. Everything is settled.</div>
            ) : invoices.map((i) => (
              <div key={i.id} data-testid={`pending-row-${i.id}`} className="grid grid-cols-[auto_auto_1.4fr_auto_auto_auto] gap-3 px-5 py-3 items-center text-sm">
                {canWrite ? (
                  <input type="checkbox" data-testid={`pending-check-${i.id}`} checked={sel.has(i.id)} onChange={() => toggleSel(i.id)}
                    className="h-4 w-4 rounded border-input accent-accent cursor-pointer" />
                ) : <span />}
                <span className="num text-xs font-bold text-primary">{i.invoice_no}</span>
                <div className="min-w-0">
                  <div className="font-medium truncate">{i.account_name || i.party_name}</div>
                  <div className="num text-xs text-muted-foreground truncate">{i.date}{i.vehicle_number ? ` · ${i.vehicle_number}` : ""}{i.driver_mobile ? ` · Driver ${i.driver_mobile}` : ""}</div>
                </div>
                <span className="num text-right text-muted-foreground">{inr(i.amount)}</span>
                <span className="num text-right font-bold text-destructive">{inr(i.balance)}</span>
                <div className="text-right">
                  {canWrite ? (
                    <button data-testid={`pending-pay-${i.id}`} onClick={() => openPay(i)}
                      className="h-8 px-3 rounded-md bg-accent text-accent-foreground text-xs font-medium hover:bg-accent/90 transition-colors">Record Payment</button>
                  ) : <span className="text-xs text-muted-foreground">—</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* single payment dialog */}
        <Dialog open={!!pay} onOpenChange={(o) => !o && setPay(null)}>
          <DialogContent className="bg-card max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl">Record Payment · {pay?.invoice_no}</DialogTitle></DialogHeader>
            {pay && (
              <div className="space-y-3">
                <div className="rounded-md bg-muted p-3 text-sm flex justify-between">
                  <span className="text-muted-foreground">{pay.party_name} · balance due</span>
                  <span className="num font-bold text-destructive">{inr(pay.balance)}</span>
                </div>
                <div>
                  <label className={label}>Payment date</label>
                  <input data-testid="pending-pay-date" type="date" className={field + " num"} value={payDate} onChange={(e) => setPayDate(e.target.value)} />
                </div>
                <div>
                  <label className={label}>Payment split <span className="text-muted-foreground font-normal">(cash + bank / UPI / QR)</span></label>
                  <div className="space-y-2">
                    {rows.map((r, i) => (
                      <div key={i} data-testid={`pending-pay-row-${i}`} className="rounded-md border border-border p-2.5 space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="grid grid-cols-4 gap-1 flex-1">
                            {PAY_METHODS.map((m) => (
                              <button key={m} type="button" data-testid={`pending-pay-method-${i}-${m}`} onClick={() => setRow(i, { method: m })}
                                className={`h-9 rounded-md text-xs uppercase border transition-colors ${r.method === m ? "bg-primary text-primary-foreground border-primary" : "bg-card border-input hover:bg-muted"}`}>{m}</button>
                            ))}
                          </div>
                          {rows.length > 1 && (
                            <button data-testid={`pending-pay-remove-${i}`} onClick={() => removeRow(i)} className="shrink-0 h-9 w-9 rounded-md border border-input flex items-center justify-center text-muted-foreground hover:text-destructive transition-colors"><X size={15} /></button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <input data-testid={`pending-pay-amount-${i}`} type="number" step="0.01" min="0" className={field} placeholder="Amount (₹)"
                            value={r.amount} onChange={(e) => setRow(i, { amount: e.target.value })} />
                          <input data-testid={`pending-pay-reference-${i}`} className={field}
                            placeholder={r.method === "cash" ? "No reference needed" : "Reference / UTR no."}
                            disabled={r.method === "cash"} value={r.reference} onChange={(e) => setRow(i, { reference: e.target.value })} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <button data-testid="pending-pay-add-split" onClick={addRow} className="mt-2 w-full h-9 rounded-md border border-dashed border-input text-sm flex items-center justify-center gap-1.5 text-muted-foreground hover:bg-muted transition-colors">
                    <Plus size={14} /> Add another method
                  </button>
                </div>
                <div className="flex justify-between items-center text-sm px-1">
                  <span className="text-muted-foreground">Total entered</span>
                  <span data-testid="pending-pay-total" className={`num font-bold ${payTotal > pay.balance + 0.001 ? "text-destructive" : "text-foreground"}`}>{inr(payTotal)}</span>
                </div>
                <div>
                  <label className={label}>Note</label>
                  <input data-testid="pending-pay-note" className={field} value={payNote} onChange={(e) => setPayNote(e.target.value)} placeholder="optional" />
                </div>
              </div>
            )}
            <DialogFooter className="gap-2">
              <button data-testid="pending-pay-save" onClick={() => submitPay(false)} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Record payment</button>
              <button data-testid="pending-pay-save-print" onClick={() => submitPay(true)} className="h-10 px-5 rounded-md bg-primary text-primary-foreground font-medium flex items-center gap-2 hover:bg-primary/90 transition-colors"><Printer size={15} /> Record &amp; Print</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* bulk settle dialog */}
        <Dialog open={!!bulk} onOpenChange={(o) => !o && setBulk(null)}>
          <DialogContent className="bg-card max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl">Settle {bulk?.invoices.length} invoices · {bulk?.party}</DialogTitle></DialogHeader>
            {bulk && (
              <div className="space-y-3">
                <div className="rounded-md border border-border max-h-40 overflow-auto divide-y divide-border/70">
                  {bulk.invoices.map((i) => (
                    <div key={i.id} className="flex justify-between px-3 py-2 text-sm">
                      <span className="num text-primary text-xs font-semibold">{i.invoice_no}</span>
                      <span className="num text-destructive font-medium">{inr(i.balance)}</span>
                    </div>
                  ))}
                </div>
                <div className="rounded-md bg-muted p-3 text-sm flex justify-between">
                  <span className="text-muted-foreground">Total balance due</span>
                  <span className="num font-bold text-destructive">{inr(bulk.total)}</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={label}>Payment date</label>
                    <input data-testid="bulk-date" type="date" className={field + " num"} value={bulkDate} onChange={(e) => setBulkDate(e.target.value)} />
                  </div>
                  <div>
                    <label className={label}>Amount paid (₹)</label>
                    <input data-testid="bulk-amount" type="number" step="0.01" min="0" className={field + " num"} value={bulkAmount} onChange={(e) => setBulkAmount(e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className={label}>Payment method</label>
                  <div className="grid grid-cols-4 gap-1">
                    {PAY_METHODS.map((m) => (
                      <button key={m} type="button" data-testid={`bulk-method-${m}`} onClick={() => setBulkMethod(m)}
                        className={`h-10 rounded-md text-xs uppercase border transition-colors ${bulkMethod === m ? "bg-primary text-primary-foreground border-primary" : "bg-card border-input hover:bg-muted"}`}>{m}</button>
                    ))}
                  </div>
                </div>
                {bulkMethod !== "cash" && (
                  <div>
                    <label className={label}>Reference / UTR no.</label>
                    <input data-testid="bulk-reference" className={field} value={bulkRef} onChange={(e) => setBulkRef(e.target.value)} placeholder="Transaction reference" />
                  </div>
                )}
                <div>
                  <label className={label}>Note</label>
                  <input data-testid="bulk-note" className={field} value={bulkNote} onChange={(e) => setBulkNote(e.target.value)} placeholder="optional" />
                </div>
                <p className="text-xs text-muted-foreground">The amount is applied to the selected invoices oldest first, and one receipt is generated for all of them.</p>
              </div>
            )}
            <DialogFooter className="gap-2">
              <button data-testid="bulk-save" onClick={() => submitBulk(false)} className="h-10 px-5 rounded-md bg-accent text-accent-foreground font-medium hover:bg-accent/90 transition-colors">Settle &amp; generate receipt</button>
              <button data-testid="bulk-save-print" onClick={() => submitBulk(true)} className="h-10 px-5 rounded-md bg-primary text-primary-foreground font-medium flex items-center gap-2 hover:bg-primary/90 transition-colors"><Printer size={15} /> Settle &amp; Print</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
